import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface OrderResult {
  order_number: string;
  total_amount: number;
  order_status: string;
  payment_status: string;
  recipient_name: string;
  tracking_number: string | null;
  delivery_company: string | null;
  ordered_at: string;
  items: { product_id: number; name: string; option: string | null; quantity: number; price: number }[];
}

const STATUS_COLORS: Record<string, string> = {
  '주문접수': '#3498db',
  '결제대기': '#f39c12',
  '배송준비': '#9b59b6',
  '배송중': '#e67e22',
  '배송완료': '#27ae60',
  '취소': '#e74c3c',
};

const OrderLookupPage: React.FC = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<OrderResult | null>(null);

  const handleLookup = async () => {
    if (!orderNumber.trim() || !phone.trim()) {
      setError('주문번호와 연락처를 모두 입력해주세요');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch('/api/storefront/orders/guest-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: orderNumber.trim(), phone: phone.trim() })
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || '주문을 찾을 수 없습니다');
        setLoading(false);
        return;
      }

      const data = await res.json();
      setResult(data);
    } catch {
      setError('서버 연결에 실패했습니다');
    }
    setLoading(false);
  };

  const formatPrice = (p: number) => p.toLocaleString() + '원';

  return (
    <>
      <Header />
      <div className="order-page">
        <div className="order-inner" style={{ maxWidth: 600 }}>
          <h2 className="order-title">주문 조회</h2>

          {!result ? (
            <div className="order-section">
              <p style={{ color: '#888', marginBottom: 20, fontSize: 14 }}>
                비회원 주문 시 받은 주문번호와 주문 시 입력한 연락처로 조회할 수 있습니다.
              </p>
              <div className="order-field">
                <label>주문번호 *</label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={e => { setOrderNumber(e.target.value); setError(''); }}
                  placeholder="WS로 시작하는 주문번호 (예: WS20260401123456)"
                />
              </div>
              <div className="order-field">
                <label>연락처 *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setError(''); }}
                  placeholder="주문 시 입력한 연락처"
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                />
              </div>

              {error && <div className="order-error">{error}</div>}

              <button
                className="order-pay-btn"
                onClick={handleLookup}
                disabled={loading}
                style={{ marginTop: 16, width: '100%' }}
              >
                {loading ? '조회 중...' : '주문 조회'}
              </button>

              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Link to="/login" style={{ color: '#666', fontSize: 14 }}>
                  회원이신가요? 로그인하여 주문내역 확인
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <div className="order-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 className="order-section-title" style={{ margin: 0 }}>주문 정보</h3>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: STATUS_COLORS[result.order_status] || '#999'
                  }}>
                    {result.order_status}
                  </span>
                </div>

                <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888' }}>주문번호</span>
                    <span style={{ fontWeight: 600 }}>{result.order_number}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888' }}>주문일시</span>
                    <span>{result.ordered_at}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888' }}>수령인</span>
                    <span>{result.recipient_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888' }}>결제 금액</span>
                    <span style={{ fontWeight: 700, color: '#e74c3c' }}>{formatPrice(result.total_amount)}</span>
                  </div>
                  {result.tracking_number && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#888' }}>운송장</span>
                      <span>{result.delivery_company} {result.tracking_number}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="order-section" style={{ marginTop: 16 }}>
                <h3 className="order-section-title">주문 상품</h3>
                <div className="order-items">
                  {result.items.map((item, idx) => (
                    <div key={idx} className="order-item" style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <div className="order-item-info" style={{ flex: 1 }}>
                        <div className="order-item-name">{item.name}</div>
                        {item.option && <div className="order-item-option" style={{ color: '#888', fontSize: 13 }}>{item.option}</div>}
                        <div style={{ fontSize: 13, color: '#888' }}>수량: {item.quantity}</div>
                      </div>
                      <div className="order-item-price" style={{ fontWeight: 600 }}>{formatPrice(item.price * item.quantity)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="order-pay-btn"
                onClick={() => setResult(null)}
                style={{ marginTop: 16, width: '100%', background: '#666' }}
              >
                다른 주문 조회
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderLookupPage;
